import { FC, useCallback, useEffect, useRef } from "react"

import { ReactComponent as IconClose } from "@common_assets/svg/cross.svg"
import { ReactComponent as IconSearch } from "@common_assets/svg/search.svg"
import { yupResolver } from "@hookform/resolvers/yup"
import { Box, debounce, IconButton, InputAdornment } from "@mui/material"
import IconWrapper from "components/common/icon/iconWrapper"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useLocation, useParams } from "react-router"

import TextField from "../common/field/textField"
import { defaultValues, validationSchema } from "./SearchbarForm.config"
import { IProps, ISearchbarFormState } from "./SearchbarForm.types"

const SearchbarForm: FC<IProps> = ({
  onSubmit,
  placeholder,
  label,
  renderAsForm = true,
  initialValue,
  clearable = false,
  inputRef,
}) => {
  const { t } = useTranslation()
  const location = useLocation()
  const param = useParams()
  const methods = useForm<ISearchbarFormState>({
    resolver: yupResolver(validationSchema),
    defaultValues: { ...defaultValues, search: initialValue ?? "" },
  })

  // The debounced submit is created once; read the latest onSubmit through a
  // ref so it never submits with a stale handler.
  const onSubmitRef = useRef(onSubmit)
  onSubmitRef.current = onSubmit

  const onChangeFunc = useCallback(
    debounce(() => {
      methods.handleSubmit((data) => onSubmitRef.current(data))()
    }, 500),
    [],
  )

  useEffect(() => () => onChangeFunc.clear(), [])

  const handleClear = () => {
    onChangeFunc.clear()
    methods.setValue("search", "")
    methods.setFocus("search")
    methods.handleSubmit(onSubmit)()
  }

  const hasValue = methods.watch("search") !== ""

  useEffect(() => {
    if (location.state !== null) {
      methods.reset()
    }
  }, [param.classId])

  return (
    <Box
      width="100%"
      component={renderAsForm ? "form" : "div"}
      onSubmit={renderAsForm ? methods.handleSubmit(onSubmit) : undefined}
    >
      <FormProvider {...methods}>
        <TextField
          name="search"
          label={label}
          placeholder={placeholder || t("search")}
          size="small"
          inputProps={{ ref: inputRef }}
          customOnChange={(e, onChange) => {
            onChange(e)
            onChangeFunc()
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconWrapper>
                  <IconSearch />
                </IconWrapper>
              </InputAdornment>
            ),
            endAdornment: clearable && hasValue && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label={t("clearSearch")}
                  onClick={handleClear}
                >
                  <IconWrapper>
                    <IconClose />
                  </IconWrapper>
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </FormProvider>
    </Box>
  )
}

export default SearchbarForm
